import styled from '@emotion/styled';
import React, { useState, useRef, useEffect } from 'react';

import { useHover, useLayer, mergeRefs } from 'react-laag';

import ResizeObserver from 'resize-observer-polyfill';

import {
  getColorValue,
  getShadow,
  getSize,
  getTextSize,
  type Size,
  u,
} from '../../utilities';
import { textSizes } from '../../variables';
import Arrange from '../arrange/arrange';
import Text from '../text/text';

type PlacementProps = {
  [key: string]:
    | 'bottom-start'
    | 'bottom-end'
    | 'bottom-center'
    | 'top-start'
    | 'top-center'
    | 'top-end'
    | 'left-end'
    | 'left-center'
    | 'left-start'
    | 'right-end'
    | 'right-center'
    | 'right-start'
    | 'center';
};

type DelaySpeed = 'immediate' | 'long';

const placements: PlacementProps = {
  topLeft: 'top-start',
  topCenter: 'top-center',
  topRight: 'top-end',
  bottomLeft: 'bottom-start',
  bottomCenter: 'bottom-center',
  bottomRight: 'bottom-end',
  leftTop: 'left-start',
  leftCenter: 'left-center',
  leftBottom: 'left-end',
  rightTop: 'right-start',
  rightCenter: 'right-center',
  rightBottom: 'right-end',
};

const tooltipMinHeight = 4;
const textHeight = textSizes.small.fontSize * textSizes.small.lineHeight;
// eslint-disable-next-line @loomhq/loom/no-consecutive-uppercase-letters-for-acronyms
const tooltipYPadding = (tooltipMinHeight - textHeight) / 2;

const TooltipBoxWrapper = styled.div<TooltipBoxProps>`
  background-color: ${getColorValue('backgroundInverse')};
  color: ${getColorValue('bodyInverse')};
  border-radius: var(--ds-radius-medium);
  font-weight: var(--ds-font-weight-medium);
  ${getTextSize('small')};
  ${getShadow('medium')};
  ${props => getSize('max-width', props.maxWidth)};
  z-index: 1100;
  padding: ${u(tooltipYPadding)} ${u(1.5)};
  z-index: ${props => props.zIndex};
`;

const ShortcutWrapper = styled.div`
  background-color: ${getColorValue('grey7')};
  border-radius: var(--ds-radius-xsmall);
  color: ${getColorValue('grey3')};
  font-weight: var(--ds-font-weight-medium);
  ${getTextSize('small')};
  padding-left: ${u(0.5)};
  padding-right: ${u(0.5)};
`;

export const ShortcutBox = ({
  children,
}: React.ComponentProps<typeof ShortcutWrapper>) => (
  <ShortcutWrapper>{children}</ShortcutWrapper>
);

export const TooltipBox = ({
  children,
  maxWidth,
  onMouseEnter,
  onMouseLeave,
  layerProps,
  zIndex,
  ...rest
}: TooltipBoxProps & React.ComponentProps<typeof TooltipBoxWrapper>) => (
  // These are being added to hide the tooltip when users hover over
  /* eslint-disable-next-line styled-components-a11y/no-static-element-interactions */
  <TooltipBoxWrapper
    maxWidth={maxWidth}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    zIndex={zIndex}
    {...layerProps}
    {...rest}
  >
    {children}
  </TooltipBoxWrapper>
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Ignoring because it was introduced through an unrelated PR that did not affect this file. When this file is touched next, it should be fixed properly
const TooltipChildren = styled.div<TooltipProps>`
  display: ${props => (props.isInline ? 'inline-block' : 'block')};
  ${props => props.verticalAlign && `vertical-align: ${props.verticalAlign}`};
  &:focus-visible {
    // Note: 0px solid transparent prevents focus rings from disappearing for -ms-high-contrast.
    // TODO(LNS-183): Provide more robust polyfill/support for :focus for older versions of Safari, which don't support :focus-visible
    outline: 0px solid transparent;
    box-shadow: var(--lns-formFieldBorderShadowFocus);
  }
`;

function getInitialDelaySpeed(delay: DelaySpeed) {
  switch (delay) {
    case 'immediate':
      return 200;
    case 'long':
      return 800;
    default:
      return 200;
  }
}

const Tooltip = ({
  ariaLive = false,
  children,
  content,
  shortcut,
  placement = 'topCenter',
  keepOpen = true,
  triggerOffset = 4,
  maxWidth = 26,
  isInline = true,
  isDisabled,
  container,
  tabIndex = 0,
  zIndex = 1100,
  verticalAlign = 'middle',
  delay = 'immediate',
  tooltipId,
  ...rest
}: TooltipProps) => {
  const [show, hoverProps] = useHover({
    delayEnter: getInitialDelaySpeed(delay),
    delayLeave: 200,
  });

  const [isOverTooltip, setIsOverTooltip] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Helps us keep the tooltip open when opened with a key press
  // instead of a mouse click.
  const [enterOrSpaceKeyDown, setEnterOrSpaceKeyDown] = useState(false);

  const focusRef = useRef();
  const isTooltipDisabled = !content || isDisabled;

  // Hide the tooltip as soon as isDisabled becomes true, and show it if a user has focused or hovered on it AND it is not disabled
  useEffect(() => {
    if (isTooltipDisabled) {
      setIsOpen(false);

      return;
    }

    const keepTooltipOpenOnHover = isOverTooltip && keepOpen;

    if (show || keepTooltipOpenOnHover) {
      setIsOpen(true);
    }

    // We have to watch this separately since hover and focus are managed separately. So, an element can be keyboard focused while another element is mouse hovered.
    // Also, we don't want to close the tooltip if the user has pressed the Enter or Space key to open it.
    if (!show && !keepTooltipOpenOnHover && !enterOrSpaceKeyDown) {
      setIsOpen(false);
    }
  }, [
    content,
    isDisabled,
    isOverTooltip,
    isTooltipDisabled,
    keepOpen,
    setIsOpen,
    show,
    enterOrSpaceKeyDown,
  ]);

  const handleFocus = () => {
    setEnterOrSpaceKeyDown(false);

    if (!isTooltipDisabled) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    setIsOpen(false);
    setEnterOrSpaceKeyDown(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const keyListener = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); // Prevent event from reaching modal or other listeners
        handleBlur();
      }
    };

    // Use capture phase (true) to ensure this fires BEFORE other listeners (including modal)
    window.addEventListener('keydown', keyListener, true);

    return () => {
      window.removeEventListener('keydown', keyListener, true);
    };
  }, [isOpen, setIsOpen]);

  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen,
    placement: placements[placement],
    ResizeObserver,
    triggerOffset,
    container,
    auto: true,
  });

  return (
    <>
      {/* eslint-disable-next-line styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions */}
      <TooltipChildren
        {...triggerProps}
        {...hoverProps}
        onClick={e => {
          // onClick callbacks react to 'Enter' and 'Space' key presses
          // as well as mouse clicks. detail > 0 if it's a mouse click/double click..
          if (e.detail === 0) {
            setEnterOrSpaceKeyDown(true);
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isInline={isInline}
        verticalAlign={verticalAlign}
        tabIndex={isTooltipDisabled ? -1 : tabIndex}
        ref={mergeRefs(triggerProps.ref, focusRef)}
      >
        {children}
      </TooltipChildren>
      {ariaLive && (
        <span className="srOnly" aria-live="polite">
          {isOpen && content}
        </span>
      )}
      {isOpen &&
        renderLayer(
          <div {...layerProps} style={{ ...layerProps.style, zIndex }}>
            <TooltipBox
              maxWidth={maxWidth}
              onMouseEnter={() => setIsOverTooltip(true)}
              onMouseLeave={() => setIsOverTooltip(false)}
              role="tooltip"
              id={tooltipId}
              {...rest}
            >
              <Arrange gap="small">
                <Text size="small" fontWeight="medium">
                  {content}
                </Text>
                {shortcut && (
                  <Arrange gap="xsmall">
                    {shortcut.map((char, index) => (
                      <ShortcutBox key={index}>{char}</ShortcutBox>
                    ))}
                  </Arrange>
                )}
              </Arrange>
            </TooltipBox>
          </div>
        )}
    </>
  );
};

export const availablePlacements = Object.keys(placements);

type TooltipBoxProps = {
  children?: React.ReactNode;
  maxWidth?: Size | [];
  onMouseEnter?: React.ReactEventHandler;
  onMouseLeave?: React.ReactEventHandler;
  layerProps?: any;
  zIndex?: number;
};

export type TooltipProps = {
  ariaLive?: boolean;
  children?: React.ReactNode;
  container?: HTMLElement | (() => HTMLElement) | string;
  content?: React.ReactNode;
  shortcut?: string[];
  isDisabled?: boolean;
  isInline?: boolean;
  keepOpen?: boolean;
  maxWidth?: Size | [];
  placement?:
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
    | 'leftTop'
    | 'leftCenter'
    | 'leftBottom'
    | 'rightTop'
    | 'rightCenter'
    | 'rightBottom';
  triggerOffset?: number;
  verticalAlign?: string;
  zIndex?: number;
  delay?: DelaySpeed;
  tabIndex?: number;
  tooltipId?: string;
};

export default Tooltip;
